import React, { Suspense, lazy } from 'react';
import { Analytics } from "@vercel/analytics/react"
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { motion, MotionConfig } from 'framer-motion';
import Layout from './Layout/Layout';
import ScrollToTop from './components/ScrollToTop';
import ScrollToTopOnRouteChange from './components/ScrollToTopOnRouteChange';
import { MotionPerformanceMonitor } from './components/OptimizedMotion';
import { PerformanceMonitor } from './components/PerformanceOptimizer';

// Lazy loading components
const Home = lazy(() => import('./Home/Home'));
const Projects = lazy(() => import('./projects/Projects'));
const ProjectDetail = lazy(() => import('./projects/ProjectDetail'));
const About = lazy(() => import('./about/About'));
const Contact = lazy(() => import('./contact/Contact'));
const Services = lazy(() => import('./services/Services'));
const Bautraeger = lazy(() => import('./services/Bautraeger'));
const Projektierung = lazy(() => import('./services/Projektierung'));
const Bausanierung = lazy(() => import('./services/Bausanierung'));
const Mitplanung = lazy(() => import('./services/Mitplanung'));
const Bauleitung = lazy(() => import('./services/Bauleitung'));
const Impressum = lazy(() => import('./legal/Impressum'));
const Datenschutz = lazy(() => import('./legal/Datenschutz'));
const AGB = lazy(() => import('./legal/AGB'));
const Cookies = lazy(() => import('./legal/Cookies'));

const LoadingFallback = () => (
  <div className="flex flex-col justify-center items-center h-screen w-full bg-gradient-to-br from-white via-silver-50 to-bronze-50">
    <motion.div
      className="mb-8"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6 }}
    >
      <img
        src="/logo/WithoutBackgroundLogo.png"
        alt="ARES WOHNBAU"
        className="h-20 w-auto opacity-90"
        loading="eager"
      />
    </motion.div>

    <motion.div
      className="relative"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.3 }}
    >
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-bronze-200 border-t-bronze-600"></div>
      <div className="absolute inset-0 animate-ping rounded-full h-12 w-12 border-2 border-bronze-300 opacity-20"></div>
    </motion.div>

    <motion.p
      className="text-gray-600 text-sm mt-6 font-serif"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.8 }}
    >
      Lädt...
    </motion.p>
  </div>
);

const App = () => {
  return (
    <MotionConfig reducedMotion="user" transition={{ type: "tween", duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}>
      <Router>
        <ScrollToTopOnRouteChange />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<Layout><Home /></Layout>} />
            <Route path="/projekte" element={<Layout><Projects /></Layout>} />
            <Route path="/projekte/:id" element={<Layout><ProjectDetail /></Layout>} />
            <Route path="/ueber-uns" element={<Layout><About /></Layout>} />
            <Route path="/kontakt" element={<Layout><Contact /></Layout>} />
            <Route path="/services" element={<Layout><Services /></Layout>} />
            <Route path="/services/bautraeger" element={<Layout><Bautraeger /></Layout>} />
            <Route path="/services/projektierung" element={<Layout><Projektierung /></Layout>} />
            <Route path="/services/bausanierung" element={<Layout><Bausanierung /></Layout>} />
            <Route path="/services/mitplanung" element={<Layout><Mitplanung /></Layout>} />
            <Route path="/services/bauleitung" element={<Layout><Bauleitung /></Layout>} />

            {/* Rechtliche Seiten */}
            <Route path="/impressum" element={<Layout><Impressum /></Layout>} />
            <Route path="/datenschutz" element={<Layout><Datenschutz /></Layout>} />
            <Route path="/agb" element={<Layout><AGB /></Layout>} />
            <Route path="/cookies" element={<Layout><Cookies /></Layout>} />

            {/* Legacy routes - redirect to new structure */}
            <Route path="/unternehmen" element={<Layout><About /></Layout>} />
            <Route path="/contact" element={<Layout><Contact /></Layout>} />
          </Routes>
        </Suspense>
        <ScrollToTop />
        {process.env.NODE_ENV === 'development' && <MotionPerformanceMonitor />}
        <PerformanceMonitor />
        <Analytics />
      </Router>
    </MotionConfig>
  );
};

export default App; 
